import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import DeleteIcon from "@material-ui/icons/Delete";
import SendIcon from "@material-ui/icons/Send";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import Alert from "@material-ui/lab/Alert";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import SUGGESTED_TEMPLATES, { resolveCatalogLang } from "./suggestedTemplates";
import {
  HEADER_FORMATS,
  MEDIA_HEADER_TYPES,
  isMediaHeaderType,
  acceptForHeaderType,
} from "../../helpers/metaTemplateHeader";

const DRAWER_WIDTH = 480;

const STATUS_COLORS = {
  APPROVED: "#16a34a",
  PENDING: "#d97706",
  REJECTED: "#dc2626",
  PAUSED: "#6b7280",
  DELETED: "#9ca3af",
  DISABLED: "#9ca3af",
  IN_APPEAL: "#7c3aed",
  REINSTATED: "#0284c7",
  LIMIT_EXCEEDED: "#dc2626",
  PENDING_DELETION: "#d97706",
};

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: 8,
  },
  headerTitle: {
    flex: 1,
    fontWeight: 600,
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
  },
  toolbar: {
    display: "flex",
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  templateRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  templateName: {
    fontWeight: 600,
    fontSize: 14,
  },
  templateMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: 4,
  },
  templateBody: {
    fontSize: 13,
    color: theme.palette.text.primary,
    marginTop: 4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  templateActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 4,
    marginTop: 8,
  },
  formField: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  empty: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(4),
  },
  suggestedPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  sampleBox: {
    border: "1px solid #fde68a",
    background: "#fffbeb",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  sampleTitle: { fontWeight: 600, fontSize: 13, color: "#92400e" },
  sampleNotice: { fontSize: 12, color: "#92400e", marginTop: 4 },
  sampleFileName: {
    fontSize: 12,
    marginTop: 8,
    color: "#374151",
    wordBreak: "break-all",
  },
  attachBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  attachTitle: { fontWeight: 600, fontSize: 13 },
  attachHint: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  suggestedItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "background 0.15s",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
}));

const LANGUAGES = ["es", "en", "pt", "it"];
const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"];

// Devuelve los números de variable únicos del body, ordenados: "{{1}} {{2}}" → [1,2]
const parseVarNumbers = (text) => {
  const matches = (text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || [];
  return [...new Set(matches.map((m) => Number(m.replace(/\D/g, ""))))].sort(
    (a, b) => a - b
  );
};

const TemplateForm = ({ whatsappId, onCreated, onCancel }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  // Idioma inicial = idioma activo de la instancia (mapeado al catálogo).
  const initialLang = resolveCatalogLang(i18n.language);
  const [form, setForm] = useState({
    name: "",
    category: "MARKETING",
    language: initialLang,
    headerFormat: "NONE",
    headerText: "",
    bodyText: "",
    footerText: "",
  });
  // Archivo de MUESTRA (feat_308): solo sirve para que Meta apruebe la plantilla.
  // En cada envío se elige el archivo real.
  const [sampleFile, setSampleFile] = useState(null);
  const isMediaHeader = MEDIA_HEADER_TYPES.includes(form.headerFormat);
  // map { varNumber: exampleValue } — sobrevive a reordenamientos de variables
  const [examples, setExamples] = useState({});
  // Catálogo de plantillas sugeridas
  const [showSuggested, setShowSuggested] = useState(false);
  // key de la plantilla del catálogo en uso (para refrescar el texto al cambiar idioma)
  const [suggestedKey, setSuggestedKey] = useState(null);

  const varNumbers = parseVarNumbers(form.bodyText);
  const isSequential = varNumbers.every((n, i) => n === i + 1);
  const allExamplesFilled =
    varNumbers.length === 0 ||
    varNumbers.every((n) => (examples[n] || "").trim().length > 0);

  const set = (key) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  // Carga una plantilla del catálogo en el formulario, en el idioma indicado.
  const applySuggested = (key, lang) => {
    const tmpl = SUGGESTED_TEMPLATES.find((t) => t.key === key);
    if (!tmpl) return;
    const loc = tmpl.langs[lang] || tmpl.langs.es;
    setForm((prev) => ({
      ...prev,
      name: key,
      category: tmpl.category,
      language: lang,
      // Las plantillas del catálogo son solo texto → encabezado en "Ninguno".
      headerFormat: "NONE",
      headerText: "",
      footerText: "",
      bodyText: loc.body,
    }));
    setSampleFile(null);
    const ex = {};
    loc.examples.forEach((v, i) => {
      ex[i + 1] = v;
    });
    setExamples(ex);
    setSuggestedKey(key);
    setShowSuggested(false);
  };

  // Cambio de idioma: si hay una plantilla del catálogo activa, re-aplica su
  // texto en el nuevo idioma; si es plantilla libre, solo cambia el idioma.
  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    if (suggestedKey) {
      applySuggested(suggestedKey, lang);
    } else {
      setForm((prev) => ({ ...prev, language: lang }));
    }
  };

  // Edición manual del body → deja de ser una plantilla del catálogo
  // (evita que un cambio de idioma pise el texto editado por el usuario).
  const handleBodyChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, bodyText: val }));
    setSuggestedKey(null);
  };

  const setExample = (varNum) => (e) => {
    const val = e.target.value;
    setExamples((prev) => ({ ...prev, [varNum]: val }));
  };

  // Cambiar el tipo de encabezado limpia lo que deja de aplicar: el texto si pasa
  // a media, el archivo de muestra si pasa a texto/ninguno.
  const handleHeaderFormatChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      headerFormat: val,
      headerText: val === "TEXT" ? prev.headerText : "",
    }));
    if (!MEDIA_HEADER_TYPES.includes(val)) setSampleFile(null);
  };

  const canSubmit =
    !loading &&
    !!form.name &&
    !!form.bodyText &&
    isSequential &&
    allExamplesFilled &&
    // Meta exige el sample para poder revisar un encabezado con archivo.
    (!isMediaHeader || !!sampleFile);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      // bodyExamples en orden {{1}},{{2}},... (Meta espera una fila con un valor por variable)
      const bodyExamples = varNumbers.map((n) => examples[n] || "");
      // multipart siempre: la ruta acepta el archivo de muestra y el resto de los
      // campos como texto (nunca base64 en JSON — el parser global corta en 100kb).
      const data = new FormData();
      data.append("name", form.name);
      data.append("category", form.category);
      data.append("language", form.language);
      data.append("headerFormat", form.headerFormat);
      if (form.headerFormat === "TEXT") data.append("headerText", form.headerText || "");
      data.append("bodyText", form.bodyText);
      data.append("footerText", form.footerText || "");
      data.append("bodyExamples", JSON.stringify(bodyExamples));
      if (isMediaHeader && sampleFile) data.append("headerSample", sampleFile);

      await api.post(`/meta-templates/${whatsappId}`, data);
      toast.success(i18n.t("metaTemplates.createSuccess"));
      toast.info(i18n.t("metaTemplates.form.pendingNotice"));
      onCreated();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  return (
    <Box>
      {!showSuggested ? (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => setShowSuggested(true)}
          style={{ marginBottom: 16 }}
        >
          {i18n.t("metaTemplates.suggested.useButton")}
        </Button>
      ) : (
        <Box className={classes.suggestedPanel}>
          <Typography style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            {i18n.t("metaTemplates.suggested.listTitle")}
          </Typography>
          <Typography style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            {i18n.t("metaTemplates.suggested.approvalNotice")}
          </Typography>
          {SUGGESTED_TEMPLATES.map((t) => {
            const loc = t.langs[form.language] || t.langs.es;
            return (
              <Box
                key={t.key}
                className={classes.suggestedItem}
                onClick={() => applySuggested(t.key, form.language)}
              >
                <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                  {i18n.t(`metaTemplates.suggested.names.${t.key}`)}
                </Typography>
                <Typography style={{ fontSize: 12, color: "#374151", whiteSpace: "pre-wrap" }}>
                  {loc.body}
                </Typography>
              </Box>
            );
          })}
          <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <Button size="small" onClick={() => setShowSuggested(false)}>
              {i18n.t("metaTemplates.form.cancel")}
            </Button>
          </Box>
        </Box>
      )}

      <TextField
        label={i18n.t("metaTemplates.form.name")}
        value={form.name}
        onChange={set("name")}
        className={classes.formField}
        size="small"
        variant="outlined"
        inputProps={{ pattern: "[a-z0-9_]+" }}
      />
      <FormControl className={classes.formField} size="small" variant="outlined">
        <InputLabel>{i18n.t("metaTemplates.form.category")}</InputLabel>
        <Select value={form.category} onChange={set("category")} label={i18n.t("metaTemplates.form.category")}>
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {i18n.t(`metaTemplates.categories.${c}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl className={classes.formField} size="small" variant="outlined">
        <InputLabel>{i18n.t("metaTemplates.form.language")}</InputLabel>
        <Select value={form.language} onChange={handleLanguageChange} label={i18n.t("metaTemplates.form.language")}>
          {LANGUAGES.map((l) => (
            <MenuItem key={l} value={l}>
              {i18n.t(`metaTemplates.languages.${l}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl className={classes.formField} size="small" variant="outlined">
        <InputLabel>{i18n.t("metaTemplates.form.headerFormat")}</InputLabel>
        <Select
          value={form.headerFormat}
          onChange={handleHeaderFormatChange}
          label={i18n.t("metaTemplates.form.headerFormat")}
        >
          {HEADER_FORMATS.map((f) => (
            <MenuItem key={f} value={f}>
              {i18n.t(`metaTemplates.form.headerFormats.${f}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {form.headerFormat === "TEXT" && (
        <TextField
          label={i18n.t("metaTemplates.form.headerText")}
          value={form.headerText}
          onChange={set("headerText")}
          className={classes.formField}
          size="small"
          variant="outlined"
        />
      )}

      {isMediaHeader && (
        <Box className={classes.sampleBox}>
          <Typography className={classes.sampleTitle}>
            {i18n.t("metaTemplates.form.headerSample")}
          </Typography>
          <Typography className={classes.sampleNotice}>
            {i18n.t("metaTemplates.form.headerSampleNotice")}
          </Typography>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            style={{ marginTop: 8 }}
          >
            {i18n.t("metaTemplates.form.headerSampleChoose")}
            <input
              type="file"
              hidden
              accept={acceptForHeaderType(form.headerFormat)}
              onChange={(e) => setSampleFile(e.target.files?.[0] || null)}
            />
          </Button>
          {sampleFile && (
            <Typography className={classes.sampleFileName}>
              📎 {sampleFile.name}
            </Typography>
          )}
        </Box>
      )}
      <TextField
        label={i18n.t("metaTemplates.form.body")}
        value={form.bodyText}
        onChange={handleBodyChange}
        className={classes.formField}
        size="small"
        variant="outlined"
        multiline
        rows={4}
        helperText={i18n.t("metaTemplates.form.bodyHint")}
      />

      {varNumbers.length > 0 && !isSequential && (
        <Typography style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}>
          {i18n.t("metaTemplates.form.varsNotSequential")}
        </Typography>
      )}

      {varNumbers.length > 0 && isSequential && (
        <Box mb={1}>
          <Typography style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            {i18n.t("metaTemplates.form.examplesHint")}
          </Typography>
          {varNumbers.map((n) => (
            <TextField
              key={n}
              label={i18n.t("metaTemplates.form.exampleFor", { var: n })}
              value={examples[n] || ""}
              onChange={setExample(n)}
              className={classes.formField}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      <TextField
        label={i18n.t("metaTemplates.form.footer")}
        value={form.footerText}
        onChange={set("footerText")}
        className={classes.formField}
        size="small"
        variant="outlined"
      />
      <Box style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={loading}>
          {i18n.t("metaTemplates.form.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {loading ? <CircularProgress size={18} /> : i18n.t("metaTemplates.form.submit")}
        </Button>
      </Box>
    </Box>
  );
};

const SendTemplateForm = ({ whatsappId, outboundPaused }) => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  // Adjunto REAL del envío (feat_308) — obligatorio si la plantilla tiene header media.
  const [mediaFile, setMediaFile] = useState(null);

  useEffect(() => {
    api
      .get(`/meta-templates/${whatsappId}`, { params: { status: "APPROVED" } })
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  }, [whatsappId]);

  const selectedTemplate = templates.find((t) => String(t.id) === String(selected));
  const needsMedia = isMediaHeaderType(selectedTemplate?.headerType);

  const handleSelectTemplate = (e) => {
    const id = e.target.value;
    setSelected(id);
    setMediaFile(null);
    const tmpl = templates.find((t) => String(t.id) === String(id));
    if (tmpl) {
      setVariables(Array(tmpl.variableCount).fill(""));
    } else {
      setVariables([]);
    }
  };

  const handleSend = async () => {
    if (!selected || !toNumber) return;
    if (needsMedia && !mediaFile) return;
    setLoading(true);
    try {
      // multipart: el adjunto nunca viaja en JSON.
      const data = new FormData();
      data.append("toNumber", toNumber);
      data.append("variables", JSON.stringify(variables));
      if (needsMedia && mediaFile) data.append("media", mediaFile);
      // R-0856 — envío manual desde Conexiones: el backend lo deja salir aunque
      // la línea tenga los envíos automáticos pausados.
      data.append("manualSend", "true");

      await api.post(`/meta-templates/${whatsappId}/${selected}/send`, data);
      toast.success(i18n.t("metaTemplates.sendSuccess"));
      setToNumber("");
      setVariables([]);
      setSelected("");
      setMediaFile(null);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  if (templates.length === 0) {
    return (
      <Typography className={classes.empty}>
        {i18n.t("metaTemplates.sendForm.noApproved")}
      </Typography>
    );
  }

  return (
    <Box>
      {outboundPaused && (
        <Alert severity="warning" className={classes.formField}>
          {i18n.t("metaTemplates.sendForm.pausedManualNotice")}
        </Alert>
      )}
      <FormControl className={classes.formField} size="small" variant="outlined">
        <InputLabel>{i18n.t("metaTemplates.sendForm.selectTemplate")}</InputLabel>
        <Select
          value={selected}
          onChange={handleSelectTemplate}
          label={i18n.t("metaTemplates.sendForm.selectTemplate")}
        >
          {templates.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.name} ({t.language})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label={i18n.t("metaTemplates.sendForm.toNumber")}
        value={toNumber}
        onChange={(e) => setToNumber(e.target.value)}
        className={classes.formField}
        size="small"
        variant="outlined"
        placeholder="5491112345678"
      />
      {variables.map((v, idx) => (
        <TextField
          key={idx}
          label={i18n.t("metaTemplates.sendForm.variables", { index: idx + 1 })}
          value={v}
          onChange={(e) => {
            const val = e.target.value;
            setVariables((prev) => {
              const next = [...prev];
              next[idx] = val;
              return next;
            });
          }}
          className={classes.formField}
          size="small"
          variant="outlined"
        />
      ))}
      {needsMedia && (
        <Box className={classes.attachBox}>
          <Typography className={classes.attachTitle}>
            {i18n.t("metaTemplates.sendForm.attachment")}
          </Typography>
          <Typography className={classes.attachHint}>
            {i18n.t(`metaTemplates.sendForm.attachmentHint.${selectedTemplate.headerType}`)}
          </Typography>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            style={{ marginTop: 8 }}
          >
            {i18n.t("metaTemplates.sendForm.attachmentChoose")}
            <input
              type="file"
              hidden
              accept={acceptForHeaderType(selectedTemplate.headerType)}
              onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
            />
          </Button>
          {mediaFile && (
            <Typography className={classes.sampleFileName}>📎 {mediaFile.name}</Typography>
          )}
        </Box>
      )}

      <Box style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={loading || !selected || !toNumber || (needsMedia && !mediaFile)}
          endIcon={loading ? <CircularProgress size={14} /> : <SendIcon />}
        >
          {i18n.t("metaTemplates.sendForm.submit")}
        </Button>
      </Box>
    </Box>
  );
};

const MetaTemplatesDrawer = ({ open, onClose, whatsapp }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const whatsappId = whatsapp?.id;

  const loadTemplates = useCallback(async () => {
    if (!whatsappId) return;
    setLoading(true);
    try {
      const r = await api.get(`/meta-templates/${whatsappId}`);
      setTemplates(r.data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [whatsappId]);

  useEffect(() => {
    if (open && whatsappId) loadTemplates();
  }, [open, whatsappId, loadTemplates]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/meta-templates/${whatsappId}/sync`);
      toast.success(i18n.t("metaTemplates.syncSuccess", { count: r.data?.synced || 0 }));
      await loadTemplates();
    } catch (err) {
      toastError(err);
    }
    setSyncing(false);
  };

  const handleDelete = async (tmplId) => {
    if (!window.confirm(i18n.t("metaTemplates.deleteConfirm"))) return;
    try {
      await api.delete(`/meta-templates/${whatsappId}/${tmplId}`);
      toast.success(i18n.t("metaTemplates.deleteSuccess"));
      setTemplates((prev) => prev.filter((t) => t.id !== tmplId));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {i18n.t("metaTemplates.drawerTitle")}
          {whatsapp && (
            <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8, color: "#6b7280" }}>
              — {whatsapp.name}
            </span>
          )}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setShowForm(false); }} indicatorColor="primary">
        <Tab label={i18n.t("metaTemplates.tabMine")} />
        <Tab label={i18n.t("metaTemplates.tabSend")} />
      </Tabs>
      <Divider />

      <div className={classes.content}>
        {tab === 0 && (
          <>
            {!showForm && (
              <div className={classes.toolbar}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {i18n.t("metaTemplates.syncButton")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setShowForm(true)}
                >
                  {i18n.t("metaTemplates.newButton")}
                </Button>
              </div>
            )}

            {showForm && (
              <Box mb={2}>
                <TemplateForm
                  whatsappId={whatsappId}
                  onCreated={() => { setShowForm(false); loadTemplates(); }}
                  onCancel={() => setShowForm(false)}
                />
                <Divider style={{ margin: "16px 0" }} />
              </Box>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress size={32} />
              </Box>
            ) : templates.length === 0 ? (
              <Typography className={classes.empty}>
                {i18n.t("metaTemplates.noTemplates")}
              </Typography>
            ) : (
              templates.map((t) => (
                <div key={t.id} className={classes.templateRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Typography className={classes.templateName}>{t.name}</Typography>
                    <Chip
                      label={i18n.t(`metaTemplates.status.${t.status}`) || t.status}
                      size="small"
                      style={{
                        background: STATUS_COLORS[t.status] || "#6b7280",
                        color: "#fff",
                        fontSize: 10,
                        height: 18,
                      }}
                    />
                  </div>
                  <Typography className={classes.templateMeta}>
                    {i18n.t(`metaTemplates.categories.${t.category}`) || t.category}
                    {" · "}
                    {i18n.t(`metaTemplates.languages.${t.language}`) || t.language}
                    {t.hasVariables && ` · ${t.variableCount} var`}
                    {/* Avisa que esta plantilla EXIGE un adjunto al enviarla. */}
                    {isMediaHeaderType(t.headerType) &&
                      ` · 📎 ${i18n.t(`metaTemplates.form.headerFormats.${t.headerType}`)}`}
                  </Typography>
                  {t.bodyText && (
                    <Typography className={classes.templateBody}>{t.bodyText}</Typography>
                  )}
                  {t.rejectionReason && (
                    <Typography style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
                      {t.rejectionReason}
                    </Typography>
                  )}
                  <div className={classes.templateActions}>
                    {(t.status === "APPROVED" || t.status === "REJECTED") && (
                      <IconButton size="small" onClick={() => handleDelete(t.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {tab === 1 && whatsappId && (
          <SendTemplateForm whatsappId={whatsappId} outboundPaused={!!whatsapp?.outboundPaused} />
        )}
      </div>
    </Drawer>
  );
};

export default MetaTemplatesDrawer;
